import { createNativeNode, emptyNativeDocument } from "../native/document";
import type { Point } from "../scene/model";
import { ImageStore, readImageFile } from "../render/image-store";

/** Clipboard-only transparent margin trim, matching the original 2048px alpha scan. */
async function imageSource(file: File, images: ImageStore) {
  const source = await readImageFile(file);
  await images.prepare([{ id: "decode", kind: "image", source }]);
  const image = images.get(source)!;
  const width = image.naturalWidth,
    height = image.naturalHeight;
  const scale = Math.min(1, 2048 / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return { source, width, height };
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  let left = canvas.width,
    top = canvas.height,
    right = -1,
    bottom = -1;
  for (let y = 0; y < canvas.height; y++)
    for (let x = 0; x < canvas.width; x++) {
      if (pixels[(y * canvas.width + x) * 4 + 3]! <= 2) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x);
      bottom = Math.max(bottom, y);
    }
  if (right < left) return { source, width, height };
  const padding = Math.max(1, Math.ceil(1 / scale));
  left = Math.max(0, Math.floor(left / scale) - padding);
  top = Math.max(0, Math.floor(top / scale) - padding);
  right = Math.min(width, Math.ceil((right + 1) / scale) + padding);
  bottom = Math.min(height, Math.ceil((bottom + 1) / scale) + padding);
  if (!left && !top && right === width && bottom === height)
    return { source, width, height };
  canvas.width = right - left;
  canvas.height = bottom - top;
  context.drawImage(
    image,
    left,
    top,
    canvas.width,
    canvas.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return {
    source: canvas.toDataURL("image/png"),
    width: canvas.width,
    height: canvas.height,
  };
}

export async function clipboardImages(
  files: readonly File[],
  images: ImageStore,
  center: Point,
) {
  const result = emptyNativeDocument();
  for (const [index, file] of files.entries()) {
    const image = await imageSource(file, images);
    const asset = {
      id: crypto.randomUUID(),
      kind: "image" as const,
      source: image.source,
    };
    const node = createNativeNode(
      crypto.randomUUID(),
      "image",
      center.x + index * 18,
      center.y + index * 18,
      image.width,
      image.height,
    );
    node.media.image = asset.id;
    node.layer.name =
      file.name
        .split(/[\\/]/)
        .pop()!
        .replace(/\.[^.]+$/, "")
        .replace(/[_-]+/g, " ")
        .trim() || "Вставленное изображение";
    result.assets.push(asset);
    result.pages[0]!.nodes.push(node);
  }
  return result;
}
